import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Ruler, Download } from "lucide-react";
import { toast } from "sonner";
import { BRAND, CATEGORIES } from "../../data/collection";
import { EASE } from "./Reveal";
import { buildOrderMessage, openOrderDM, openOrderWA } from "../../lib/order";
import { WhatsAppMark, ReplyNudge } from "./QuickChat";
import SizeGuide from "./SizeGuide";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL", "Custom"];

export default function Lookbook({ product, onClose }) {
  const [size, setSize] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    setSize(null);
    setGuideOpen(false);
  }, [product]);

  useEffect(() => {
    if (product) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [product]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleOrder = async () => {
    const msg = buildOrderMessage(product, size);
    const copied = await openOrderDM(msg);
    if (copied) {
      toast.success("Order details copied", {
        description: "Just paste it into the Instagram chat that opened.",
      });
    } else {
      toast("Opening Instagram DM", {
        description: `Mention "${product.name}"${size ? ` · ${size}` : ""} in your message.`,
      });
    }
  };

  const handleOrderWA = () => {
    const msg = buildOrderMessage(product, size);
    openOrderWA(msg);
    toast.success("Opening WhatsApp", {
      description: `Your ${product.name}${size ? ` · ${size}` : ""} message is prefilled.`,
    });
  };

  const downloadCover = () => {
    const a = document.createElement("a");
    a.href = `/covers/${product.id}.jpg`;
    a.download = `Keilani-${product.name}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Cover downloaded", {
      description: "Share it on WhatsApp or an Instagram story.",
    });
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="lookbook-overlay"
          className="fixed inset-0 z-[100] bg-[#050505] text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            className="h-full w-full overflow-y-auto no-scrollbar"
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            exit={{ y: 40 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            {/* top bar */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#050505]/80 px-6 py-5 backdrop-blur-md md:px-12">
              <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/50">
                {product.index} · {(CATEGORIES[product.category] || CATEGORIES.onam).label}
              </span>
              <button
                data-testid="lookbook-close"
                onClick={onClose}
                className="group flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.3em] text-white/70 transition-colors hover:text-white"
              >
                Close
                <X className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" strokeWidth={1.2} />
              </button>
            </div>

            <div className="grid gap-0 lg:grid-cols-2">
              {/* gallery */}
              <div className="flex flex-col gap-1 bg-neutral-950 p-1">
                {product.video && (
                  <div className="relative w-full overflow-hidden" data-testid="lookbook-video">
                    <video
                      src={product.video}
                      poster={product.cover}
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full object-cover"
                    />
                    <span className="pointer-events-none absolute left-4 top-4 font-body text-[10px] uppercase tracking-[0.28em] text-white/80">
                      In motion
                    </span>
                  </div>
                )}
                {product.gallery.map((src, i) => (
                  <div key={i} className="w-full overflow-hidden">
                    <img src={src} alt={`${product.name} ${i + 1}`} className="w-full object-cover" />
                  </div>
                ))}
              </div>

              {/* info — sticky */}
              <div className="relative">
                <div className="lg:sticky lg:top-[73px] flex min-h-[60vh] flex-col justify-center px-6 py-14 md:px-14">
                  <p className="font-body text-[11px] uppercase tracking-[0.35em] text-[var(--kln-gold)]">
                    {product.tagline}
                  </p>
                  {product.badge && (
                    <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-[var(--kln-gold)]/50 bg-white/5 px-3 py-1 font-body text-[10px] uppercase tracking-[0.22em] text-[var(--kln-gold)]">
                      <span className="text-[10px] leading-none">&#10022;</span>
                      {product.badge}
                    </span>
                  )}
                  <h2 className="mt-5 font-display text-6xl md:text-7xl font-light leading-none">
                    {product.name}
                  </h2>
                  <p className="mt-3 font-serif-editorial text-2xl font-light italic text-white/60">
                    {product.meaning}
                  </p>
                  {product.verse && (
                    <p
                      data-testid="lookbook-verse"
                      className="mt-1 font-serif-editorial text-base italic text-[var(--kln-gold)]/80"
                    >
                      &mdash; {product.verse}
                    </p>
                  )}

                  <div className="mt-6 flex items-baseline gap-3" data-testid="lookbook-price">
                    <span className="font-display text-3xl font-light">
                      &#8377;{product.price.toLocaleString("en-IN")}
                    </span>
                    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
                      {product.priceNote}
                    </span>
                  </div>

                  <p className="mt-7 max-w-md font-body text-sm md:text-base font-light leading-relaxed text-white/70">
                    {product.description}
                  </p>

                  {product.colours && (
                    <p
                      data-testid="lookbook-colours"
                      className="mt-4 font-serif-editorial text-base italic text-[var(--kln-gold)]/85"
                    >
                      {product.colours}
                    </p>
                  )}

                  {/* features */}
                  <ul className="mt-7 space-y-2.5">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 font-body text-sm font-light text-white/70">
                        <span className="text-[var(--kln-gold)] text-xs leading-none">&#10022;</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* sizes */}
                  <div className="mt-10">
                    <div className="flex items-center justify-between">
                      <p className="font-body text-[11px] uppercase tracking-[0.3em] text-white/40">
                        Select Size
                      </p>
                      <button
                        data-testid="lookbook-size-guide"
                        onClick={() => setGuideOpen(true)}
                        className="group flex items-center gap-1.5 font-body text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white"
                      >
                        <Ruler className="h-3.5 w-3.5" strokeWidth={1.3} />
                        Size guide
                      </button>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2.5" data-testid="lookbook-sizes">
                      {SIZES.map((s) => (
                        <button
                          key={s}
                          data-testid={`size-option-${s}`}
                          onClick={() => setSize(s)}
                          className={`flex h-11 min-w-11 items-center justify-center rounded-full border px-4 font-body text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                            size === s
                              ? "border-white bg-white text-black"
                              : "border-white/25 text-white/80 hover:border-white/60"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 font-serif-editorial text-base italic text-white/40">
                      Made to your measure — tailored on request.
                    </p>
                  </div>

                  {/* CTA — prefilled WhatsApp + Instagram DM */}
                  <div className="mt-10">
                    <div className="flex flex-wrap gap-3">
                      <button
                        data-testid="lookbook-order-whatsapp"
                        onClick={handleOrderWA}
                        className="group inline-flex flex-1 min-w-[220px] items-center justify-center gap-3 rounded-full bg-[#25D366] px-6 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px]"
                      >
                        <WhatsAppMark className="h-4 w-4" />
                        {size ? `WhatsApp · ${size}` : "WhatsApp us"}
                      </button>
                      <button
                        data-testid="lookbook-order-dm"
                        onClick={handleOrder}
                        className="group inline-flex flex-1 min-w-[220px] items-center justify-center gap-3 rounded-full bg-white px-6 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-black transition-colors duration-300 hover:bg-[var(--kln-gold)]"
                      >
                        {size ? `Instagram DM · ${size}` : "Instagram DM"}
                        <ArrowUpRight
                          className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                          strokeWidth={1.5}
                        />
                      </button>
                    </div>
                    <ReplyNudge testid="lookbook-wa-nudge" className="mt-3" />
                  </div>
                  <p className="mt-4 font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
                    We prefill your message — just paste &amp; send · {BRAND.whatsappNumber} · {BRAND.instagramHandle}
                  </p>

                  <button
                    data-testid="lookbook-download-cover"
                    onClick={downloadCover}
                    className="group mt-6 inline-flex w-fit items-center gap-2.5 font-body text-[11px] uppercase tracking-[0.22em] text-white/60 transition-colors duration-300 hover:text-white"
                  >
                    <Download className="h-4 w-4" strokeWidth={1.3} />
                    <span className="relative">
                      Download shareable cover
                      <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-white transition-transform duration-500 group-hover:scale-x-100" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          <SizeGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
